import React, { useEffect, useMemo, useRef } from "react";
import { graphql, useStaticQuery } from "gatsby";
import parse, { attributesToProps, domToReact } from "html-react-parser";
import { Box } from "@chakra-ui/react";
import { TwitterTweetEmbed } from "react-twitter-embed";
import ChapterNav from "../../customBlocks/chapterNav/ChapterNav";
import CtaBox from "../../customBlocks/ctaBox/CtaBox";
import SocialLockedContent from "./socialLockedContent";
import NewsletterLockedContent from "./newsletterLockedContent";
import PreviewTooltip from "./previewTooltip";
import Footnote from "../footnotes/footnote";
import LightBox from "./LightBox";
import CustomerLifetimeValueCalculator from "./customerLifetimeValueCalculator";

const PostContent = ({ post, children }) => {
    // Use refs for mutable variables to avoid unnecessary rerenders
    const chapterArrayRef = useRef([]);
    const chapterIndexRef = useRef(1);
    const figureIndexRef = useRef(0);

    // Handle anchor link clicks
    useEffect(() => {
        const handleClick = (e) => {
            if (e.target.matches('.content [href^="#"]')) {
                e.preventDefault();
                const href = e.target.getAttribute("href").replace('#', '');
                const elem = document.getElementById(href);
                if (elem) {
                    elem.scrollIntoView({ behavior: "smooth" });
                }
            }
        };
        document.addEventListener("click", handleClick);
        return () => document.removeEventListener("click", handleClick);
    }, []);

    // Extract path from URL helper
    function extractPath(url) {
        const noProtocol = url.replace(/^https?:\/\//, '');
        const firstSlashIndex = noProtocol.indexOf('/');
        if (firstSlashIndex === -1) return '/';
        return noProtocol.slice(firstSlashIndex);
    }

    // Static query for fast previews and mailing data
    const fastPreviewData = useStaticQuery(graphql`
        query fastPreviewArrayData {
          mailings : allWp {
              nodes {
                  globalSettings {
                      globalSettingsFields {
                          mailerGroup{
                              default
                              name
                              class
                              group
                          }
                      }
                  }
              }
          }
          posts: allWpPost(filter: {seo: {fastPreviewTitle: {ne: null}}}) {
            nodes {
              uri
              title
              seo {
                fastPreviewTitle
                fastPreviewDescription
                seoKeyword
              }
            }
          }
          kbase: allWpKnowledgeBase(filter: {seo: {fastPreviewTitle: {ne: null}}}) {
            nodes {
              uri
              seo {
                fastPreviewTitle
                fastPreviewDescription
                seoKeyword
              }
            }
          }
        }`);

    const fastPreviewArray = useMemo(() => 
        fastPreviewData.posts.nodes.concat(fastPreviewData.kbase.nodes),
        [fastPreviewData.posts.nodes, fastPreviewData.kbase.nodes]
    );

    // Create lookup map for fast preview data
    const previewLookup = useMemo(() => {
        const lookup = {};
        fastPreviewArray.forEach(item => {
            const path = item.uri;
            lookup[path] = item;
        });
        return lookup;
    }, [fastPreviewArray]);

    // Constants
    const SKIP_PREVIEW = useMemo(() => ['mailto:', 'tel:', '/static/', '#'], []);
    
    // Get storage values
    const mailUnlocked = useMemo(() => 
        (typeof window !== 'undefined') && localStorage.getItem('mailUnlocked'),
        []
    );
    
    const socialUnlocked = useMemo(() => 
        (typeof window !== 'undefined') && JSON.parse(localStorage.getItem('socialUnlocked')) || [],
        []
    );

    const lang = post.language?.code ?? 'PL';

    // Handle image lightbox rendering
    const handleImageLightbox = (domNode) => {
        figureIndexRef.current++;
        const childNodes = domNode?.children ?? null;

        if (domNode.name === 'figure' && childNodes[0]?.name !== 'a') {
            return <LightBox figure={domNode} figureIndex={figureIndexRef.current} />;
        } else if (childNodes[0]?.name === 'figure' || childNodes[1]?.name === 'figure') {
            const childNode = childNodes[0]?.name === 'figure' ? childNodes[0] : childNodes[1];
            return <LightBox figure={childNode} figureIndex={figureIndexRef.current} />;
        } else if (childNodes[0]?.name === 'a') {
            return <LightBox figure={childNodes[0]} figureIndex={figureIndexRef.current} />;
        }
        return null;
    };

    // Handle chapter navigation
    const handleChapter = (domNode) => {
        chapterArrayRef.current.push(domNode);
        
        if (chapterIndexRef.current === 1) {
            const existingClass = domNode.attribs.class;
            const updatedClass = `${existingClass} first-chapter`;
            const updatedProps = attributesToProps(domNode.attribs);
            updatedProps.className = updatedClass;
            
            const updatedElement = React.createElement(
                domNode.name, 
                updatedProps, 
                domToReact(domNode.children)
            );
            
            chapterIndexRef.current++;
            return updatedElement;
        }
        return undefined;
    };

    // Parse the HTML content with optimized replace function
    const reactElements = useMemo(() => parse(children || "", {
        replace: (domNode) => {
            if (!domNode.attribs) return undefined;
            const { class: className, href, id } = domNode.attribs;

            // Twitter script tag handling
            if (domNode.name === "script" && domNode.attribs.src === 'https://platform.twitter.com/widgets.js') {
                return <Box />;
            }

            // Twitter embed handling
            if (className?.includes("twitter-tweet")) {
                const twitterEmbedUrl = domNode.children[2]?.attribs?.href;
                if (twitterEmbedUrl) {
                    const twitterEmbedId = twitterEmbedUrl.split('/')[5]?.split('?')[0];
                    return <TwitterTweetEmbed tweetId={twitterEmbedId} />;
                }
                return null;
            }

            // Social locked content
            if (className?.includes("social-locked-content")) {
                return socialUnlocked.includes(post.uri) 
                    ? domToReact(domNode.children)
                    : <SocialLockedContent children={domNode.children} post={post} socialUnlocked={socialUnlocked} />;
            }

            // Newsletter locked content
            if (className?.includes("newsletter-locked-content")) {
                if (mailUnlocked) return domToReact(domNode.children);
                
                const type = className.replace('newsletter-locked-content', '').trim();
                const mailing = fastPreviewData.mailings.nodes[0].globalSettings.globalSettingsFields.mailerGroup
                    .find(node => node.class === type || node.default === true);
                
                return <NewsletterLockedContent children={domNode.children} post={post} mailing={mailing} />;
            }

            // Customer lifetime value calculator
            if (className?.includes("customer-lifetime-value")) {
                return <CustomerLifetimeValueCalculator children={domNode.children} post={post} />;
            }

            // Link preview tooltips
            if (domNode.name === "a" && href) {
                if (SKIP_PREVIEW.some(item => href.includes(item))) return undefined;
                
                const path = extractPath(href);
                const previewData = previewLookup[path];
                
                if (previewData) {
                    return <PreviewTooltip 
                        data={[previewData]} 
                        attribs={domNode.attribs} 
                        children={domNode.children}
                    />;
                }
            }

            // Footnote handling
            if (domNode.name === "sup") {
                return <Footnote>{domNode.children[0]}</Footnote>;
            }

            // Image and lightbox handling
            if (className?.includes("wp-block-image")) {
                return handleImageLightbox(domNode);
            }

            // CTA box handling
            if (className === "custom-block-cta") {
                return <CtaBox box={domNode} />;
            }

            // Chapter navigation handling
            if (className === "custom-block-chapter") {
                return handleChapter(domNode);
            }

            // Chapters navigation block
            if (id === "chapters-navigation") {
                return <ChapterNav chapters={chapterArrayRef.current} lang={lang} />;
            }

            return undefined;
        }
    }), [
        children, 
        socialUnlocked, 
        mailUnlocked, 
        previewLookup, 
        SKIP_PREVIEW,
        post, 
        lang,
        fastPreviewData.mailings.nodes
    ]);

    return reactElements;
};

export default PostContent;
